import type { EventDraftResponse, EventMinAge } from '@pulsify/shared/dto'
import { MapPin, Search } from 'lucide-react'
import { type ReactNode, useId, useState } from 'react'
import { useListings } from '~/entities/listing/model/use-listings'
import type { TicketTypeInput } from '~/entities/ticket/api/tickets-api'
import { useTicketSettings } from '~/entities/ticket/model/use-tickets'
import { formatEventLongDay, formatEventTimeRange } from '~/features/events/lib/event-date-format'
import { draftTicketType, type EventEntry } from '~/features/events/lib/event-entry'
import { TicketTypeDialog } from '~/features/events/tickets/ticket-type-dialog'
import { TicketTypeList } from '~/features/events/tickets/ticket-type-list'
import { TagSelector } from '~/features/shared/components/tag-selector'
import { TagBadges } from '~/features/shared/components/view/tags-display'
import * as m from '~/paraglide/messages'
import { useGeocoding } from '~/shared/lib/useGeocoding'
import type { GeocodingSuggestion } from '~/shared/types/geoCoding'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Switch } from '~/shared/ui/switch'
import {
  addressHiddenAllowed,
  type ChatDetails,
  type DetailQuestion,
  scheduleFrom,
  splitSchedule,
  ticketTypesForEntry,
} from './chat-details'
import { Chip, Chips, GlassCard } from './chat-ui'

type Answer = (patch: Partial<ChatDetails>, label: string) => void

export function WhenQuestion({
  details,
  active,
  onAnswer,
  onSkip,
}: {
  details: ChatDetails
  active: boolean
  onAnswer: Answer
  onSkip: () => void
}) {
  const id = useId()
  const [inputs, setInputs] = useState(() => splitSchedule(details))
  const schedule = scheduleFrom(inputs.date, inputs.start, inputs.end)
  if (!active) return null
  return (
    <GlassCard className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-date`} className="text-muted-foreground">
            {m.aiFlowWhenDate()}
          </Label>
          <Input
            id={`${id}-date`}
            type="date"
            value={inputs.date}
            onChange={(event) => setInputs({ ...inputs, date: event.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-start`} className="text-muted-foreground">
            {m.aiFlowWhenStart()}
          </Label>
          <Input
            id={`${id}-start`}
            type="time"
            value={inputs.start}
            onChange={(event) => setInputs({ ...inputs, start: event.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-end`} className="text-muted-foreground">
            {m.aiFlowWhenEnd()}
          </Label>
          <Input
            id={`${id}-end`}
            type="time"
            value={inputs.end}
            onChange={(event) => setInputs({ ...inputs, end: event.target.value })}
          />
        </div>
      </div>
      <Chips>
        <Chip
          tone="go"
          disabled={!schedule}
          onClick={() => schedule && onAnswer(schedule, scheduleLabel(schedule))}
        >
          {m.aiFlowNext()}
        </Chip>
        <Chip tone="quiet" onClick={onSkip}>
          {m.aiFlowSkip()}
        </Chip>
      </Chips>
    </GlassCard>
  )
}

function scheduleLabel(schedule: Pick<ChatDetails, 'startTime' | 'endTime'>): string {
  if (!schedule.startTime) return m.aiFlowReviewMissing()
  const start = new Date(schedule.startTime)
  const end = schedule.endTime ? new Date(schedule.endTime) : start
  return `${formatEventLongDay(start)}, ${formatEventTimeRange(start, end)}`
}

function placeLabel(details: ChatDetails): string {
  const location = details.location
  if (details.locationPending) {
    return location?.city ? `${m.aiFlowPlaceLater()}, ${location.city}` : m.aiFlowPlaceLater()
  }
  if (!location) return m.aiFlowReviewMissing()
  return [location.name, location.street, location.city].filter(Boolean).join(', ')
}

export function placePrompt(details: ChatDetails): string {
  const found = details.location?.name || details.location?.street
  return found ? m.aiFlowAskPlaceUnpinned({ place: found }) : m.aiFlowAskPlace()
}

export function PlaceQuestion({
  businessId,
  details,
  active,
  onAnswer,
  onSkip,
}: {
  businessId: string
  details: ChatDetails
  active: boolean
  onAnswer: Answer
  onSkip: () => void
}) {
  const id = useId()
  const { listings } = useListings(businessId)
  const { suggest, retrieve } = useGeocoding()
  const [query, setQuery] = useState(() =>
    [details.location?.name, details.location?.street, details.location?.city]
      .filter(Boolean)
      .join(', '),
  )
  const [suggestions, setSuggestions] = useState<GeocodingSuggestion[]>([])
  if (!active) return null

  const search = (text: string) => {
    setQuery(text)
    if (text.trim().length < 3) {
      setSuggestions([])
      return
    }
    suggest({ query: text, limit: 5 })
      .then(setSuggestions)
      .catch(() => setSuggestions([]))
  }

  const pick = async (suggestion: GeocodingSuggestion) => {
    const found = await retrieve({ mapboxId: suggestion.mapboxId }).catch(() => null)
    if (!found) return
    const location = {
      name: suggestion.name === found.street ? (details.location?.name ?? null) : suggestion.name,
      street: found.street ?? '',
      city: found.city ?? '',
      country: found.country ?? '',
      coordinates: { lat: found.lat, lng: found.lng },
    }
    setSuggestions([])
    onAnswer({ location, listingId: null, locationPending: false }, suggestion.fullAddress)
  }

  return (
    <GlassCard className="flex flex-col gap-3">
      {listings.length > 0 ? (
        <Chips>
          {listings.slice(0, 4).map((listing) => (
            <Chip
              key={listing.id}
              onClick={() =>
                onAnswer(
                  {
                    listingId: listing.id,
                    locationPending: false,
                    location: {
                      name: listing.location.name || listing.name,
                      street: listing.location.street,
                      city: listing.location.city,
                      country: listing.location.country,
                      coordinates: listing.location.coordinates ?? null,
                    },
                  },
                  listing.name,
                )
              }
            >
              <MapPin className="size-3.5" aria-hidden />
              {listing.name}
            </Chip>
          ))}
        </Chips>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-search`} className="text-muted-foreground">
          {m.aiFlowPlaceSearch()}
        </Label>
        <div className="relative">
          <Search
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            id={`${id}-search`}
            className="pl-9"
            value={query}
            autoComplete="off"
            onChange={(event) => search(event.target.value)}
          />
        </div>
        {suggestions.length > 0 ? (
          <ul className="flex flex-col divide-y overflow-hidden rounded-xl border border-border">
            {suggestions.map((suggestion) => (
              <li key={suggestion.mapboxId}>
                <button
                  type="button"
                  onClick={() => pick(suggestion)}
                  className="flex w-full flex-col items-start px-3 py-2 text-left outline-none hover:bg-muted focus-visible:bg-muted"
                >
                  <span className="text-sm">{suggestion.name}</span>
                  <span className="text-muted-foreground text-xs">{suggestion.fullAddress}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <Chips>
        <Chip
          onClick={() =>
            onAnswer(
              {
                locationPending: true,
                listingId: null,
                location: details.location ? { ...details.location, coordinates: null } : null,
              },
              m.aiFlowPlaceLater(),
            )
          }
        >
          {m.aiFlowPlaceLater()}
        </Chip>
        <Chip tone="quiet" onClick={onSkip}>
          {m.aiFlowSkip()}
        </Chip>
      </Chips>
    </GlassCard>
  )
}

const ENTRY_LABEL: Record<EventEntry, () => string> = {
  free: m.entryFree,
  tickets: m.entryTickets,
  signup: m.entrySignup,
  link: m.entryLink,
}

export function entryPrompt(details: ChatDetails): string {
  return details.ticketTypes.length > 0
    ? m.aiFlowAskEntryFound({ count: String(details.ticketTypes.length) })
    : m.entryDescription()
}

function entryPatch(
  entry: EventEntry,
  details: ChatDetails,
  price: EventDraftResponse['price'],
): Partial<ChatDetails> {
  if (entry === 'tickets') {
    return {
      entry,
      ticketTypes: ticketTypesForEntry(details, price, m.ticketPreviewDefaultType()),
    }
  }
  return { entry }
}

export function EntryQuestion({
  details,
  price,
  active,
  onAnswer,
  onSkip,
}: {
  details: ChatDetails
  price: EventDraftResponse['price']
  active: boolean
  onAnswer: Answer
  onSkip: () => void
}) {
  if (!active) return null
  const options: EventEntry[] = details.ticketUrl
    ? ['tickets', 'free', 'signup', 'link']
    : ['tickets', 'free', 'signup']
  return (
    <Chips>
      {options.map((entry, index) => (
        <Chip
          key={entry}
          tone={index === 0 ? 'go' : 'default'}
          onClick={() => onAnswer(entryPatch(entry, details, price), ENTRY_LABEL[entry]())}
        >
          {ENTRY_LABEL[entry]()}
        </Chip>
      ))}
      <Chip tone="quiet" onClick={onSkip}>
        {m.aiFlowSkip()}
      </Chip>
    </Chips>
  )
}

export function CategoryQuestion({
  details,
  active,
  onAnswer,
  onSkip,
}: {
  details: ChatDetails
  active: boolean
  onAnswer: Answer
  onSkip: () => void
}) {
  const [category, setCategory] = useState(details.mainCategoryId)
  const [subs, setSubs] = useState(details.subCategoryTags)
  if (!active) return null
  return (
    <GlassCard className="flex flex-col gap-3">
      <TagSelector
        selectedCategoryId={category}
        selectedSubCategoryIds={subs}
        onCategoryChange={(next) => {
          setCategory(next)
          setSubs([])
        }}
        onSubCategoriesChange={setSubs}
        maxSubCategories={3}
      />
      <Chips>
        <Chip
          tone="go"
          disabled={!category || subs.length === 0}
          onClick={() =>
            onAnswer({ mainCategoryId: category, subCategoryTags: subs }, m.aiFlowCategoryChosen())
          }
        >
          {m.aiFlowNext()}
        </Chip>
        <Chip tone="quiet" onClick={onSkip}>
          {m.aiFlowSkip()}
        </Chip>
      </Chips>
    </GlassCard>
  )
}

const MIN_AGE_ALL = 'all'
type MinAgeChoice = typeof MIN_AGE_ALL | '16' | '18'

function ReviewGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0">
      <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">{title}</h4>
      {children}
    </section>
  )
}

function ReviewLine({
  label,
  value,
  missing,
  onChange,
}: {
  label: string
  value: ReactNode
  missing?: boolean
  onChange?: () => void
}) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="w-20 shrink-0 text-muted-foreground">{label}</span>
      <span className={missing ? 'min-w-0 flex-1 text-destructive' : 'min-w-0 flex-1'}>
        {value}
      </span>
      {onChange ? (
        <button
          type="button"
          onClick={onChange}
          className="shrink-0 font-medium text-brand text-xs underline-offset-4 hover:underline"
        >
          {m.aiFlowChange()}
        </button>
      ) : null}
    </div>
  )
}

function ToggleLine({
  id,
  label,
  checked,
  onChange,
}: {
  id: string
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <Label htmlFor={id} className="font-normal">
        {label}
      </Label>
      <Switch id={id} checked={checked} onCheckedChange={(next) => onChange(next === true)} />
    </div>
  )
}

export function ReviewCard({
  businessId,
  details,
  price,
  onChange,
  onAsk,
}: {
  businessId: string
  details: ChatDetails
  price: EventDraftResponse['price']
  onChange: (patch: Partial<ChatDetails>) => void
  onAsk: (question: DetailQuestion) => void
}) {
  const id = useId()
  const settings = useTicketSettings(businessId)
  const [dialog, setDialog] = useState<{ open: boolean; index: number | null }>({
    open: false,
    index: null,
  })
  const types = details.ticketTypes.map(draftTicketType)
  const setTypes = (next: TicketTypeInput[]) => onChange({ ticketTypes: next })
  const entryOptions: EventEntry[] = details.ticketUrl
    ? ['free', 'tickets', 'signup', 'link']
    : ['free', 'tickets', 'signup']
  const minAge: MinAgeChoice =
    details.minAge === null ? MIN_AGE_ALL : (String(details.minAge) as MinAgeChoice)

  return (
    <GlassCard className="flex flex-col divide-y">
      <ReviewGroup title={m.aiFlowReviewBasics()}>
        <ReviewLine
          label={m.eventFieldDates()}
          value={scheduleLabel(details)}
          missing={!details.startTime}
          onChange={() => onAsk('when')}
        />
        <ReviewLine
          label={m.location()}
          value={placeLabel(details)}
          missing={!details.location && !details.locationPending}
          onChange={() => onAsk('place')}
        />
        <ReviewLine
          label={m.eventFieldCategory()}
          value={
            details.mainCategoryId ? (
              <TagBadges
                categoryId={details.mainCategoryId}
                subCategoryIds={details.subCategoryTags}
              />
            ) : (
              m.aiFlowReviewMissing()
            )
          }
          missing={!details.mainCategoryId}
          onChange={() => onAsk('category')}
        />
      </ReviewGroup>

      <ReviewGroup title={m.entryTitle()}>
        <SegmentedControl<EventEntry>
          size="sm"
          label={m.entryDescription()}
          value={details.entry ?? ('' as EventEntry)}
          options={entryOptions.map((entry) => ({ value: entry, label: ENTRY_LABEL[entry]() }))}
          onChange={(entry) => onChange(entryPatch(entry, details, price))}
        />
        {details.entry === 'tickets' ? (
          <div className="flex flex-col gap-2">
            <TicketTypeList
              types={types}
              confirmDelete={false}
              onEdit={(type) => setDialog({ open: true, index: type.sort })}
              onDelete={(type) =>
                setTypes(details.ticketTypes.filter((_, index) => index !== type.sort))
              }
            />
            <Chips>
              <Chip
                disabled={!settings.data}
                onClick={() => setDialog({ open: true, index: null })}
              >
                {m.ticketsAddType()}
              </Chip>
            </Chips>
            {settings.data ? (
              <TicketTypeDialog
                draft
                open={dialog.open}
                onOpenChange={(open) => setDialog((current) => ({ ...current, open }))}
                type={dialog.index === null ? null : (types[dialog.index] ?? null)}
                settings={settings.data}
                onSave={async (input) => {
                  const index = dialog.index
                  setTypes(
                    index === null
                      ? [...details.ticketTypes, input]
                      : details.ticketTypes.map((type, at) => (at === index ? input : type)),
                  )
                }}
              />
            ) : null}
          </div>
        ) : null}
        {details.entry === 'link' ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              aria-label={m.entryLinkLabel()}
              value={details.ticketUrl}
              onChange={(event) => onChange({ ticketUrl: event.target.value })}
            />
            <SegmentedControl
              size="sm"
              label={m.ticketLinkKindLabel()}
              value={details.ticketUrlKind}
              options={[
                { value: 'tickets', label: m.ticketLinkKindTickets() },
                { value: 'registration', label: m.ticketLinkKindSignup() },
              ]}
              onChange={(ticketUrlKind) => onChange({ ticketUrlKind })}
            />
          </div>
        ) : null}
        {details.entry === 'tickets' ? null : (
          <div className="flex items-center justify-between gap-3 text-sm">
            <Label htmlFor={`${id}-capacity`} className="font-normal">
              {m.eventFieldCapacity()}
            </Label>
            <Input
              id={`${id}-capacity`}
              type="number"
              min={1}
              className="h-8 w-24"
              value={details.capacity ?? ''}
              onChange={(event) =>
                onChange({ capacity: event.target.value ? Number(event.target.value) : null })
              }
            />
          </div>
        )}
      </ReviewGroup>

      <ReviewGroup title={m.settingsGroupAccess()}>
        <SegmentedControl
          size="sm"
          label={m.eventVisibilityLabel()}
          value={details.privacy}
          options={[
            { value: 'public', label: m.eventVisibilityPublic() },
            { value: 'request', label: m.eventVisibilityRequest() },
            { value: 'private', label: m.visibilityPrivateShort() },
          ]}
          onChange={(privacy) => onChange({ privacy })}
        />
        {addressHiddenAllowed(details) ? (
          <ToggleLine
            id={`${id}-address`}
            label={m.addressHiddenLabel()}
            checked={details.addressHidden}
            onChange={(addressHidden) => onChange({ addressHidden })}
          />
        ) : null}
        <ToggleLine
          id={`${id}-live`}
          label={m.liveRoomFeature()}
          checked={details.liveRoom}
          onChange={(liveRoom) => onChange({ liveRoom })}
        />
      </ReviewGroup>

      <ReviewGroup title={m.settingsGroupAudience()}>
        <div className="flex items-center justify-between gap-3 text-sm">
          <span>{m.minAgeFeature()}</span>
          <SegmentedControl<MinAgeChoice>
            size="sm"
            label={m.minAgeFeature()}
            value={minAge}
            options={[
              { value: MIN_AGE_ALL, label: m.minAgeEveryone() },
              { value: '16', label: m.minAgeBadge({ age: '16' }) },
              { value: '18', label: m.minAgeBadge({ age: '18' }) },
            ]}
            onChange={(next) =>
              onChange({ minAge: next === MIN_AGE_ALL ? null : (Number(next) as EventMinAge) })
            }
          />
        </div>
        <ToggleLine
          id={`${id}-students`}
          label={m.studentsOnlyFeature()}
          checked={details.studentsOnly}
          onChange={(studentsOnly) => onChange({ studentsOnly })}
        />
        <Input
          aria-label={m.audienceNoteLabel()}
          placeholder={m.audienceNoteLabel()}
          maxLength={200}
          value={details.audienceNote}
          onChange={(event) => onChange({ audienceNote: event.target.value })}
        />
      </ReviewGroup>
    </GlassCard>
  )
}
